import Vue from 'vue';
import { gsap } from 'gsap';
import Hammer from 'hammerjs';

Vue.component('fancy-slider-component', {
    props: {
        slides: {
            type: Array,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            currentIndex: 1,
            cursor: null,
            direction: 0,
            inTransition: false,
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.cursor = this.$el.querySelector('.fancy-slider__cursor-holder');
        this.isTouchDevice = document.querySelector('html').classList.contains('touchevents');

        const slides = [...this.$el.querySelectorAll('.fancy-slider__slide-image')];
        slides.forEach((element, index) => {
            if (index > 0) {
                element.classList.add('--revealed');
                return;
            }

            element.classList.add('--visible');
        });

        this.box = this.$el.getBoundingClientRect();
        document.addEventListener('mousemove', this.desktopCursor);

        if (this.isTouchDevice) {
            this.initHammerTime();
        }
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        desktopCursor(e) {
            // const target = e.target.getBoundingClientRect();
            this.box = this.$el.getBoundingClientRect();
            // eslint-disable-next-line no-mixed-operators
            const x = Math.floor((e.clientX - this.box.left) / (this.box.right - this.box.left) * this.box.width);
            // eslint-disable-next-line no-mixed-operators
            const y = Math.floor((e.clientY - this.box.top) / (this.box.bottom - this.box.top) * this.box.height);

            gsap.to(this.cursor, {
                x: x - (this.cursor.getBoundingClientRect().width / 2),
                y: y - (this.cursor.getBoundingClientRect().height / 2),
            });
            this.arrowDirection(x, this.box);
        },

        onMouseEnter() {
            gsap.to(this.cursor, {
                duration: 0.3,
                scale: 1,
            });
        },

        onMouseLeave() {
            gsap.to(this.cursor, {
                duration: 0.3,
                scale: 0,
            });
        },

        arrowDirection(cursorX, el) {
            const { width } = el;

            const svg = this.cursor.querySelector('svg');
            const tl = gsap.timeline();

            if (cursorX > width / 2) {
                gsap.to(svg, 1, {
                    rotate: 0,
                    ease: 'power4.out',
                });

                return;
            }

            tl
            .to(svg, 1, {
                rotate: -180,
                ease: 'power4.out',
            }, '<');
        },

        getSlideDirection(e) {
            if (this.isTouchDevice) {
                return;
            }

            const dimensions = window.innerWidth;

            if (e.clientX > dimensions / 2) {
                this.direction = 1;
                this.nextSlide();
                return;
            }

            this.direction = 0;
            this.prevSlide();
        },

        nextSlide() {
            if (this.inTransition) {
                return;
            }

            if (this.currentIndex === this.slides.length) {
                this.currentIndex = 1;
                return;
            }

            // eslint-disable-next-line no-plusplus
            this.currentIndex++;
        },

        prevSlide() {
            if (this.inTransition) {
                return;
            }

            if (this.currentIndex === 1) {
                this.currentIndex = this.slides.length;
                return;
            }

            // eslint-disable-next-line no-plusplus
            this.currentIndex--;
        },

        goToSlide(index) {
            this.direction = this.currentIndex > index ? 0 : 1;
            this.currentIndex = index;
        },

        // --------
        // ENTERING
        // --------

        beforeEnter(el) {
            this.inTransition = true;
            // eslint-disable-next-line no-param-reassign
            el.style.zIndex = 2;
            console.log(`[Before enter]: ${el.dataset.index}`);
            console.log(`[Current index]; ${this.currentIndex}`);
            const img = el.querySelector('.fancy-slider__slide-image img');
            const textHolder = el.querySelector('.fancy-slider__slide-content');
            const box = this.$el.querySelector('.fancy-slider__slide-image.--visible').getBoundingClientRect();

            img.classList.add('--js-transition');
            if (this.direction === 1) {
                gsap.set(el.querySelector('.fancy-slider__slide-image'), {
                    clip: `rect(0px, ${box.width}px, ${box.height}px, ${box.height}px)`,
                });
            } else {
                gsap.set(el.querySelector('.fancy-slider__slide-image'), {
                    clip: `rect(0px, 0px, ${box.height}px, 0px)`,
                });
            }

            gsap.set(img, {
                scale: 1.4,
                xPercent: () => (this.direction === 1 ? 50 : -50),
            });

            gsap.set(textHolder, {
                x: () => (this.direction === 1 ? 75 : -75),
                opacity: 0,
            });
        },

        enter(el, done) {
            console.log(`[Entering]: ${el.dataset.index}`);
            const tl = gsap.timeline();
            const img = el.querySelector('.fancy-slider__slide-image img');
            const textHolder = el.querySelector('.fancy-slider__slide-content');
            const box = this.$el.querySelector('.fancy-slider__slide-image.--visible').getBoundingClientRect();

            if (this.direction === 1) {
                gsap.to(el.querySelector('.fancy-slider__slide-image'), {
                    duration: 1.2,
                    ease: 'power4.out',
                    clip: `rect(0px, ${box.width}px, ${box.height}px, 0px)`,
                });
            } else {
                gsap.to(el.querySelector('.fancy-slider__slide-image'), {
                    duration: 1.2,
                    ease: 'power4.out',
                    clip: `rect(0px, ${box.width}px, ${box.height}px, 0px)`,
                });
            }

            tl
            .to(img, 1.5, {
                scale: 1,
                xPercent: 0,
                ease: 'power4.out',
            })
            .to(textHolder, 1, {
                x: 0,
                opacity: 1,
                ease: 'power4.out',
                onComplete: done,
            }, '-=0.9');
        },

        afterEnter(el) {
            // eslint-disable-next-line no-param-reassign
            el.style.zIndex = '';
            // eslint-disable-next-line no-param-reassign
            el.querySelector('.fancy-slider__slide-image').style.clip = '';
            el.querySelector('.fancy-slider__slide-image').classList.add('--visible');
            this.inTransition = false;
        },

        beforeEnterNumber(el) {
            const number = el;

            gsap.set(number, {
                x: this.direction === 1 ? 75 : -75,
                opacity: 0,
            });
        },

        enterNumber(el, done) {
            const number = el;

            gsap.to(number, 1, {
                x: 0,
                opacity: 1,
                ease: 'power4.out',
                delay: 0.4,
                onComplete: done,
            });
        },

        // --------
        // LEAVING
        // --------

        beforeLeave(el) {
            console.log(`[Before leave]: ${el.dataset.index}`);
            const img = el.querySelector('.fancy-slider__slide-image img');
            const textHolder = el.querySelector('.fancy-slider__slide-content');

            img.classList.add('--js-transition');
            gsap.set(img, {
                scale: 1,
                xPercent: 0,
            });

            gsap.set(textHolder, {
                x: 0,
                opacity: 1,
            });
        },

        leave(el, done) {
            console.log(`[Leaving]: ${el.dataset.index}`);
            const tl = gsap.timeline();
            const img = el.querySelector('.fancy-slider__slide-image img');
            const textHolder = el.querySelector('.fancy-slider__slide-content');

            tl
            .to(img, 1.5, {
                scale: 1.4,
                xPercent: () => (this.direction === 1 ? -25 : 25),
                ease: 'power4.out',
                onComplete: done,
            })
            .to(textHolder, 1, {
                x: () => (this.direction === 1 ? -75 : 75),
                opacity: 0,
                ease: 'power4.out',
            }, '<');
        },

        afterLeave(el) {
            el.querySelector('.fancy-slider__slide-image').classList.remove('--visible');
        },

        beforeLeaveNumber(el) {
            const number = el;

            gsap.set(number, {
                x: 0,
                opacity: 1,
            });
        },

        leaveNumber(el, done) {
            const number = el;

            gsap.to(number, 1, {
                x: this.direction === 1 ? -75 : 75,
                opacity: 0,
                ease: 'power4.out',
                delay: 0.4,
                onComplete: done,
            });
        },

        /**
         * Initialize Hammertime
         *
         * @return {void}
         */
        initHammerTime() {
            this.hammertime = new Hammer(this.$el, {});
            this.hammertime.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

            this.hammertime.on('swipeleft', (ev) => {
                this.direction = 1;
                this.nextSlide();
            });

            this.hammertime.on('swiperight', (ev) => {
                this.direction = 0;
                this.prevSlide();
            });
        },
    },
});
